/* ==========================================================================
   CADOO POST-ADD CONFIRMATION MODAL - controller.

   Opens after a Main Gift (or a Both product bought as a main gift) is added,
   confirms exactly what went into the bag, and offers up to four approved,
   relevant, sellable add-ons.

   OWNERSHIP
   The cart is owned by assets/cadoo.js and stays there: this file never talks
   to /cart.js or /cart/change.js itself, never computes a total of its own,
   and never re-implements the free-delivery rule. It calls
   window.Cadoo.addToCart / refreshCart / cartSubtotal / freeShipState, so the
   modal, the cart drawer, the cart page and the Delivery & Gifting Details
   step can never disagree about a number.

   The overlay is owned by assets/cadoo.js too - window.Cadoo.open / close give
   the shared open stack, focus trap, Escape, scroll lock and focus
   restoration. There is no second overlay system.

   THE THREE-PART GATE
   A candidate is shown only if all three hold:
     1. APPROVED    - it came out of sections/cadoo-addon-offer.liquid, which
                      reads custom.compatible_add_ons and nothing else.
     2. RELEVANT    - it passes the context rules below against what the
                      customer explicitly chose.
     3. SELLABLE    - already enforced server-side, and revalidated against
                      /cart/add.js the moment Add is pressed.
   Context can only ever narrow an approved list. It can never introduce a
   product, so no occasion, recipient or interest match can smuggle in
   something an administrator did not approve.

   CONTEXT RULES (generic - no product, occasion or add-on type is named)
     Occasion   If the customer explicitly stated an occasion, a candidate
                declaring a conflicting occasion is suppressed. With no stated
                occasion, every approved candidate remains eligible. The
                originating gift's own occasions are irrelevant: supporting an
                occasion is not proof that the customer is shopping for it.
     Recipient  HARD GATE ONLY WHEN KNOWN. If the customer stated a recipient
                and the candidate declares recipients, one must match. An
                unknown recipient never rejects anything.
     Interest   RANKING ONLY. An overlap lifts a candidate; a mismatch or a
                missing interest never rejects one.

   RANKING, in order: occasion match, recipient match, interest overlap, then
   the administrator's own order in custom.compatible_add_ons. Ties fall back
   to that order, so the result is stable and deterministic on every render.
   ========================================================================== */
(function () {
  'use strict';

  var MAX_SHOWN = 4;
  var TYPE_MAX_SHOWN = 10;
  var LOADING_DELAY_MS = 220;
  var OFFER_SECTION = 'cadoo-addon-offer';

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  var modal = null;
  var els = {};

  /* Every open gets a token. A response, a redraw or a cart refresh that
     belongs to an older token is dropped, so a second Quick Add landing while
     the first is still in flight can never repaint the newer modal with the
     older product's data. */
  var token = 0;
  var controller = null;
  var loadingTimer = null;
  var initiator = null;
  var origin = null;
  var offerCards = [];
  var contextSnapshot = { occasion: [], recipient: [], interest: [] };
  var activeType = 'recommended';
  var eligibleEntries = [];
  var deferredChain = Promise.resolve();

  function ready() {
    modal = document.getElementById('cd-addon');
    if (!modal) return false;
    els = {
      summary: $('[data-cd-amodal-summary]', modal),
      ship: $('[data-cd-amodal-ship]', modal),
      live: $('[data-cd-amodal-live]', modal),
      alert: $('[data-cd-amodal-alert]', modal),
      recs: $('[data-cd-amodal-recs]', modal),
      types: $('[data-cd-amodal-types]', modal),
      cards: $('[data-cd-amodal-cards]', modal),
      loading: $('[data-cd-amodal-loading]', modal),
      total: $('[data-cd-amodal-total]', modal)
    };
    return true;
  }

  function api() { return window.Cadoo || null; }
  function money(cents) {
    return window.CadooMoney ? window.CadooMoney(cents) : String(cents);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------------- context ---------------- */

  function contextOf(dim) {
    if (!window.CadooContext) return [];
    try { return window.CadooContext.getDimension(dim) || []; } catch (e) { return []; }
  }

  function listAttr(el, name) {
    var raw = (el.getAttribute(name) || '').trim();
    if (!raw) return [];
    return raw.split(',').map(function (v) { return v.trim().toLowerCase(); })
      .filter(function (v) { return !!v; });
  }

  function overlaps(a, b) {
    for (var i = 0; i < a.length; i++) { if (b.indexOf(a[i]) > -1) return true; }
    return false;
  }

  /* Steps 9 and 10 of the candidate pipeline. Steps 1-8 already happened in
     sections/cadoo-addon-offer.liquid. The returned entries stay in memory so
     a type tab can repaint the same approved cards without reopening the modal
     or requesting the offer section again. */
  function candidateType(card) {
    var label = (card.getAttribute('data-addon-type') || '').trim();
    return {
      key: label.toLowerCase(),
      label: label
    };
  }

  function evaluateCandidates(cards) {
    var ctxOccasion = contextSnapshot.occasion || [];
    var ctxRecipient = contextSnapshot.recipient || [];
    var ctxInterest = contextSnapshot.interest || [];
    var kept = [];
    var seen = Object.create(null);

    cards.forEach(function (card) {
      var id = card.getAttribute('data-product-id') || '';
      if (!id || seen[id]) return;

      var occasions = listAttr(card, 'data-occasions');
      var recipients = listAttr(card, 'data-recipients');
      var interests = listAttr(card, 'data-interests');
      var type = candidateType(card);
      var occasionMatch = false;

      /* Compatibility is the approval gate. Explicit shopping context only
         ranks approved candidates now; it must never make an administrator's
         compatible add-on disappear from the modal. */
      if (occasions.length && ctxOccasion.length && overlaps(occasions, ctxOccasion)) {
        occasionMatch = true;
      }

      var recipientMatch = false;
      if (recipients.length && ctxRecipient.length && overlaps(recipients, ctxRecipient)) {
        recipientMatch = true;
      }

      var interestMatch = interests.length && ctxInterest.length && overlaps(interests, ctxInterest);

      seen[id] = true;
      kept.push({
        el: card,
        id: id,
        rank: parseInt(card.getAttribute('data-rank'), 10) || 0,
        occasions: occasions,
        typeKey: type.key,
        typeLabel: type.label,
        score: (occasionMatch ? 4 : 0) + (recipientMatch ? 2 : 0) + (interestMatch ? 1 : 0)
      });
    });

    kept.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.rank - b.rank;
    });
    return kept;
  }

  function typeChoices(entries) {
    var choices = [];
    var seen = Object.create(null);
    entries.forEach(function (entry) {
      if (!entry.typeKey || seen[entry.typeKey]) return;
      seen[entry.typeKey] = true;
      choices.push({ key: entry.typeKey, label: entry.typeLabel });
    });
    return choices;
  }

  /* ---------------- rendering ---------------- */

  function renderSummary(line) {
    if (!els.summary) return;
    if (!line) { els.summary.innerHTML = ''; return; }

    var variant = line.variant_title && line.variant_title !== 'Default Title'
      ? line.variant_title
      : '';
    var meta = [];
    if (variant) meta.push(esc(variant));
    meta.push('Qty ' + (parseInt(line.quantity, 10) || 1));

    var img = line.image
      ? '<img class="cd-amodal__thumb" src="' + esc(sized(line.image, 200)) + '" alt="" width="84" height="84">'
      : '<span class="cd-amodal__thumb" aria-hidden="true"></span>';

    els.summary.innerHTML =
      img +
      '<div class="cd-amodal__sum-body">' +
      '<p class="cd-amodal__sum-title">' + esc(line.product_title || line.title || '') + '</p>' +
      '<p class="cd-amodal__sum-meta">' + meta.join(' &middot; ') + '</p>' +
      '<p class="cd-amodal__sum-price">' + money(line.original_line_price) + '</p>' +
      '</div>';
  }

  function sized(url, w) {
    if (!url) return '';
    return url.indexOf('?') > -1 ? url + '&width=' + w : url + '?width=' + w;
  }

  /* Free delivery is rendered from the cart's own numbers through
     window.Cadoo.freeShipState, the same call the drawer footer makes, so the
     two can never drift apart or use different thresholds. */
  function renderShip(cart) {
    if (!els.ship) return;
    var cadoo = api();
    var state = cadoo && cadoo.freeShipState ? cadoo.freeShipState(cart) : null;

    if (!state || !state.enabled) {
      els.ship.hidden = true;
      els.ship.innerHTML = '';
      return;
    }
    els.ship.hidden = false;
    var messageHtml = String(state.messageHtml || '')
      .replace(/🎉/g,
        '<span class="cd-amodal__ship-icon" aria-hidden="true">' +
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/>' +
        '</svg></span>')
      .replace(/🚚/g,
        '<span class="cd-amodal__ship-icon cd-amodal__ship-icon--delivery" aria-hidden="true">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M2 6.5h12v9H2zM14 10h4l4 4v1.5h-8z"/>' +
        '<circle cx="6" cy="18" r="1.5"/><circle cx="18" cy="18" r="1.5"/>' +
        '</svg></span>');
    els.ship.innerHTML =
      '<div class="cd-ship-bar__text">' + messageHtml + '</div>' +
      '<div class="cd-ship-bar__track"><div class="' + state.fillClass +
      '" style="width:' + state.percent + '%"></div></div>';
  }

  function renderTotals(cart) {
    var cadoo = api();
    if (els.total && cadoo && cadoo.cartSubtotal) {
      els.total.textContent = money(cadoo.cartSubtotal(cart));
    }
    renderShip(cart);
  }

  function say(message) {
    if (els.live) els.live.textContent = message || '';
  }

  function showAlert(message) {
    if (!els.alert) return;
    if (!message) { els.alert.hidden = true; els.alert.textContent = ''; return; }
    els.alert.textContent = message;
    els.alert.hidden = false;
  }

  /* ---------------- offer loading ---------------- */

  function offerUrl(productUrl) {
    var base = String(productUrl || '').split('#')[0].split('?')[0];
    if (!base) return '';
    return base + '?section_id=' + encodeURIComponent(OFFER_SECTION);
  }

  function parseOffer(text) {
    if (!text) return null;
    var doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.querySelector('[data-cd-offer]');
  }

  /* The product page's own offer, rendered with the page into an inert
     <template> by snippets/cadoo-addon-modal.liquid from the same markup the
     offer section returns. A fresh copy is cloned for every open, so no card
     element is ever shared between opens, and it is matched on the product id
     of what was actually added, so another product's add can never use it. */
  function pageOffer(productId) {
    if (!productId) return null;
    var templates = $$('template[data-cd-offer-preload]');
    for (var i = 0; i < templates.length; i++) {
      var tpl = templates[i];
      if (tpl.getAttribute('data-product-id') !== String(productId)) continue;
      if (!tpl.content) return null;
      return tpl.content.cloneNode(true).querySelector('[data-cd-offer]');
    }
    return null;
  }

  /* Quick Add knows the product URL at the moment of the click, so its offer
     is requested then, alongside the cart add rather than after it. By the
     time the add has succeeded the answer is normally already here and the
     modal opens in its final layout. Each request is used at most once, by
     the next open or role check for the same URL; if it has not arrived yet,
     that caller waits on this same request rather than sending a second one,
     exactly as it would have waited on its own. A failed request is never
     reused: the caller asks again, as before. */
  var earlyOffers = Object.create(null);

  function requestEarly(productUrl) {
    var url = offerUrl(productUrl);
    if (!url) return;
    var entry = { done: false, el: null };
    entry.promise = fetch(url)
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(parseOffer)
      .catch(function () { return null; })
      .then(function (el) {
        entry.done = true;
        entry.el = el;
        return el;
      });
    earlyOffers[url] = entry;
  }

  function takeEarly(productUrl, onlyIfArrived) {
    var url = offerUrl(productUrl);
    var entry = url ? earlyOffers[url] : null;
    if (!entry) return null;
    if (entry.done && !entry.el) { delete earlyOffers[url]; return null; }
    if (onlyIfArrived && !entry.done) return null;
    delete earlyOffers[url];
    return entry;
  }

  function fetchOffer(productUrl, myToken) {
    var url = offerUrl(productUrl);
    if (!url) return Promise.resolve(null);

    var early = takeEarly(productUrl, false);
    if (early) {
      return early.promise.then(function (el) {
        return myToken === token ? el : null;
      });
    }

    var options = {};
    if (typeof window.AbortController === 'function') {
      controller = new window.AbortController();
      options.signal = controller.signal;
    }

    return fetch(url, options)
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (text) {
        if (myToken !== token || !text) return null;
        return parseOffer(text);
      })
      .catch(function () { return null; });
  }

  function cancelLoadingTimer() {
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
  }

  function showLoading(on) {
    if (els.loading) els.loading.hidden = !on;
  }

  function scheduleLoading(myToken) {
    cancelLoadingTimer();
    loadingTimer = setTimeout(function () {
      loadingTimer = null;
      if (myToken === token) showLoading(true);
    }, LOADING_DELAY_MS);
  }

  function renderTypeTabs(entries) {
    if (!els.types) return;
    var choices = typeChoices(entries);
    if (choices.length < 2) {
      els.types.hidden = true;
      els.types.innerHTML = '';
      activeType = 'recommended';
      return;
    }

    var valid = activeType === 'recommended' || choices.some(function (choice) {
      return choice.key === activeType;
    });
    if (!valid) activeType = 'recommended';

    els.types.hidden = false;
    var html = '<button type="button" class="cd-amodal__type' +
      (activeType === 'recommended' ? ' is-active' : '') +
      '" data-cd-amodal-type="recommended" aria-pressed="' +
      (activeType === 'recommended' ? 'true' : 'false') + '">Recommended</button>';
    choices.forEach(function (choice) {
      html += '<button type="button" class="cd-amodal__type' +
        (activeType === choice.key ? ' is-active' : '') +
        '" data-cd-amodal-type="' + esc(choice.key) + '" aria-pressed="' +
        (activeType === choice.key ? 'true' : 'false') + '">' + esc(choice.label) + '</button>';
    });
    els.types.innerHTML = html;
  }

  function entriesForActiveType(entries) {
    if (activeType === 'recommended') return entries;
    return entries.filter(function (entry) { return entry.typeKey === activeType; });
  }

  function renderCurrentRecommendations() {
    renderTypeTabs(eligibleEntries);
    var limit = activeType === 'recommended' ? MAX_SHOWN : TYPE_MAX_SHOWN;
    var cardCount = paintCards(entriesForActiveType(eligibleEntries), limit);

    if (!cardCount) {
      hideRecommendations();
      return false;
    }
    if (els.recs) els.recs.hidden = false;
    return true;
  }

  function paintCards(entries, limit) {
    if (!els.cards) return 0;
    var visible = entries.slice(0, limit);
    els.cards.innerHTML = '';
    visible.forEach(function (entry) {
      entry.el.hidden = false;
      els.cards.appendChild(entry.el);
    });
    els.cards.classList.toggle('cd-amodal__cards--single', visible.length === 1);
    if (visible.length) {
      els.cards.style.setProperty('--cd-ac-count', String(visible.length));
    } else {
      els.cards.style.removeProperty('--cd-ac-count');
    }
    return visible.length;
  }

  function paintRecommendations(offerEl, myToken) {
    if (myToken !== token) return;
    cancelLoadingTimer();
    showLoading(false);

    if (!offerEl || !els.recs || !els.cards) { hideRecommendations(); return; }

    if (!offerCards.length) offerCards = $$('[data-cd-addon]', offerEl);
    eligibleEntries = evaluateCandidates(offerCards);
    renderCurrentRecommendations();
  }

  function hideRecommendations() {
    cancelLoadingTimer();
    if (els.recs) els.recs.hidden = true;
    if (els.types) { els.types.hidden = true; els.types.innerHTML = ''; }
    if (els.cards) {
      els.cards.innerHTML = '';
      els.cards.classList.remove('cd-amodal__cards--single');
      els.cards.style.removeProperty('--cd-ac-count');
    }
    offerCards = [];
    eligibleEntries = [];
    activeType = 'recommended';
  }

  /* ---------------- adding an add-on ---------------- */

  function variantOf(card) {
    var control = $('[data-cd-addon-variant]', card);
    if (!control) return null;
    var id = parseInt(control.value, 10);
    return isNaN(id) ? null : id;
  }

  function cardError(card, message) {
    var el = $('[data-cd-addon-err]', card);
    if (!el) return;
    if (!message) { el.hidden = true; el.textContent = ''; return; }
    el.textContent = message;
    el.hidden = false;
  }

  function addAddon(card) {
    var cadoo = api();
    if (!cadoo || !cadoo.addToCart || card.classList.contains('is-busy')) return;

    var button = $('[data-cd-addon-add]', card);
    var label = $('[data-cd-addon-add-label]', card);
    var variantId = variantOf(card);
    var title = card.getAttribute('data-product-title') || 'that add-on';

    if (!variantId) {
      cardError(card, 'Please choose an option first.');
      return;
    }

    cardError(card, '');
    card.classList.add('is-busy');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    if (label) label.textContent = 'Adding…';

    /* Informational only. The association points at a real, persistent parent
       - the originating product - so it still means something on the order.
       No property is written on the main gift line, so identical main gifts
       keep merging and keep their own quantity. Add-ons are independent
       purchasable lines: nothing here couples their quantity to anything. */
    var properties = { _cadoo_addon_source: 'post_add_modal' };
    if (origin && origin.productId) properties._cadoo_addon_for_product_id = String(origin.productId);
    if (origin && origin.productTitle) properties._cadoo_addon_for_title = origin.productTitle;

    cadoo.addToCart({ items: [{ id: variantId, quantity: 1, properties: properties }] })
      .then(function () {
        return cadoo.refreshCart();
      })
      .then(function (cart) {
        renderTotals(cart);
        card.classList.add('is-added');
        if (label) label.textContent = '✓ Added';
        say(title + ' added to your gift bag.');
      })
      .catch(function (err) {
        cardError(card, (err && err.message) || 'We could not add that right now. Please try again.');
        if (label) label.textContent = '+ Add';
        card.classList.remove('is-added');
      })
      .then(function () {
        card.classList.remove('is-busy');
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = card.classList.contains('is-added');
        }
      });
  }

  function resetAddedState(card) {
    cardError(card, '');
    if (!card.classList.contains('is-added')) return;
    card.classList.remove('is-added');
    var button = $('[data-cd-addon-add]', card);
    var label = $('[data-cd-addon-add-label]', card);
    if (button) button.disabled = false;
    if (label) label.textContent = '+ Add';
  }

  /* Keeps the price and image in step with the chosen variant. Only sellable
     variants are ever rendered, so any choice here is a valid one. */
  function syncVariant(card) {
    var select = $('select[data-cd-addon-variant]', card);
    if (!select) return;
    var option = select.options[select.selectedIndex];
    if (!option) return;

    var price = parseInt(option.getAttribute('data-price'), 10);
    var priceEl = $('[data-cd-addon-price]', card);
    if (priceEl && !isNaN(price)) priceEl.textContent = money(price);

    var image = option.getAttribute('data-image');
    var imgEl = $('[data-cd-addon-img]', card);
    if (imgEl && image) imgEl.src = image;

    resetAddedState(card);
  }

  function syncSwatch(card, swatch) {
    var input = $('[data-cd-addon-variant]', card);
    if (!input || !swatch) return;

    var variantId = swatch.getAttribute('data-variant-id');
    if (!variantId) return;
    input.value = variantId;

    var price = parseInt(swatch.getAttribute('data-price'), 10);
    var priceEl = $('[data-cd-addon-price]', card);
    if (priceEl && !isNaN(price)) priceEl.textContent = money(price);

    var selectedValue = $('[data-cd-addon-selected-value]', card);
    if (selectedValue) selectedValue.textContent = swatch.getAttribute('data-value') || '';

    var image = swatch.getAttribute('data-image');
    var imgEl = $('[data-cd-addon-img]', card);
    if (imgEl && image) imgEl.src = image;

    $$('[data-cd-addon-swatch]', card).forEach(function (item) {
      var selected = item === swatch;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    resetAddedState(card);
  }

  /* ---------------- open / close ---------------- */

  /* The initiating control may have been inside a drawer that has just closed,
     in which case focusing it would trap focus in something invisible. Fall
     back to the cart trigger, which is always present and always visible. */
  function isUsableFocusTarget(control) {
    return !!(control && control.isConnected && control.offsetParent !== null &&
      !control.disabled && control.getAttribute('aria-disabled') !== 'true');
  }

  function focusAnchor(control) {
    if (isUsableFocusTarget(control)) return control;
    var fallback = document.querySelector('[data-cd-cart-trigger]');
    return isUsableFocusTarget(fallback) ? fallback : null;
  }

  function lineFor(cart, item) {
    if (!cart || !cart.items || !item) return item || null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === item.key) return cart.items[i];
    }
    return item;
  }

  function abortPending() {
    if (controller) {
      try { controller.abort(); } catch (e) {}
      controller = null;
    }
  }

  /* `preloaded` is an offer element a caller has ALREADY fetched and validated
     for this exact product - openDeferred() has one in hand by the time it can
     decide the modal should open at all. Passing it through means that one
     response is used rather than requested twice. It is painted under this
     open's own token, so every token and abort protection behaves exactly as
     it does on the normal path, and a caller without one is unaffected. */
  function open(item, info, preloaded) {
    if (!modal && !ready()) return false;
    var cadoo = api();
    if (!cadoo) return false;

    /* Close any open overlay FIRST, and only then take a token. Closing the
       modal over itself - a second Quick Add while this one is still up - runs
       onClosed(), which invalidates the current token on purpose; taking ours
       before that would hand this open a token that is stale the moment it is
       issued, and its own recommendations would be discarded on arrival.

       Closing first also keeps the two overlays from stacking at all: the
       overlay contract keeps a single last-focused element, so a modal on top
       of an open drawer would lose the control to return focus to. */
    if (cadoo.closeAll) cadoo.closeAll();

    token += 1;
    var myToken = token;
    abortPending();

    origin = {
      productId: (info && info.productId) || (item && item.product_id) || null,
      productTitle: (info && info.productTitle) || (item && item.product_title) || '',
      url: (info && info.url) || (item && item.url) || '',
      source: (info && info.source) || 'unknown'
    };
    initiator = (info && info.initiator) || null;
    contextSnapshot = {
      occasion: contextOf('occasion').slice(),
      recipient: contextOf('recipient').slice(),
      interest: contextOf('interest').slice()
    };
    activeType = 'recommended';

    showAlert('');
    say('');
    hideRecommendations();
    showLoading(false);

    var cart = cadoo.getCartState ? cadoo.getCartState() : null;
    renderSummary(lineFor(cart, item));
    renderTotals(cart);
    say('Added to your gift bag.');

    var anchor = focusAnchor(initiator);
    if (anchor && anchor.focus) { try { anchor.focus(); } catch (e) {} }

    /* Settle the add-on area BEFORE the modal becomes visible whenever the
       answer is already in hand - the caller's preloaded offer, the product
       page's own rendered offer, or a Quick Add request that has already
       arrived - so the first visible frame is the final layout. Only when it
       is genuinely still on its way does the modal open first and fill in
       afterwards, exactly as before. */
    if (!preloaded) preloaded = pageOffer(origin.productId);
    if (!preloaded && origin.url) {
      var arrived = takeEarly(origin.url, true);
      if (arrived) preloaded = arrived.el;
    }
    if (preloaded) paintRecommendations(preloaded, myToken);

    document.body.classList.add('cd-amodal-open');
    cadoo.open(modal);

    if (!preloaded && origin.url) {
      scheduleLoading(myToken);
      fetchOffer(origin.url, myToken).then(function (offerEl) {
        paintRecommendations(offerEl, myToken);
      });
    }
    return true;
  }

  /* Used when the originating product's role is not knowable from the markup
     that started the add - today, the Saved drawer and the Saved page, whose
     cards are built from /products/<handle>.js, which carries no metafields.
     The offer section is asked first, because it reports the role it read from
     the product itself; the modal then opens only for a main gift. A failure
     resolves to "not a main gift", so the customer always gets the ordinary
     drawer confirmation rather than a wrong claim. */
  function openDeferred(item, info, fallback) {
    var run = function () { return resolveDeferred(item, info, fallback); };
    var queued = deferredChain.then(run, run);
    deferredChain = queued.catch(function () {});
    return queued;
  }

  function resolveDeferred(item, info, fallback) {
    if (!modal && !ready()) {
      if (fallback) fallback();
      return Promise.resolve();
    }

    token += 1;
    var myToken = token;
    abortPending();

    var url = (info && info.url) || (item && item.url) || '';
    if (!url) {
      if (fallback) fallback();
      return Promise.resolve();
    }

    return fetchOffer(url, myToken).then(function (offerEl) {
      if (myToken !== token) return;
      var role = offerEl ? (offerEl.getAttribute('data-origin-role') || '') : '';
      if (!isMainGiftRole(role)) {
        if (fallback) fallback();
        return;
      }

      var merged = {};
      for (var k in info) { if (Object.prototype.hasOwnProperty.call(info, k)) merged[k] = info[k]; }
      merged.role = role;
      if (!merged.productId && offerEl) merged.productId = offerEl.getAttribute('data-origin-id');
      if (!merged.productTitle && offerEl) merged.productTitle = offerEl.getAttribute('data-origin-title');

      /* The originating quick-add control remains busy until this entire
         confirmation chain returns to assets/cadoo.js. Keeping release
         ownership there prevents the Saved page from submitting a second add
         while this role check is still resolving. */

      /* Hand the response we already have straight to open(), so the offer
         section is requested once per add, not twice. */
      if (!open(item, merged, offerEl) && fallback) fallback();
    });
  }

  function isMainGiftRole(role) {
    var value = String(role || '').trim().toLowerCase();
    return value === 'main gift' || value === 'both';
  }

  function onClosed() {
    abortPending();
    token += 1;
    document.body.classList.remove('cd-amodal-open');
    hideRecommendations();
    showLoading(false);
    showAlert('');
    say('');
    origin = null;
    initiator = null;
    contextSnapshot = { occasion: [], recipient: [], interest: [] };
  }

  /* ---------------- wiring ---------------- */

  function init() {
    if (!ready()) return;

    modal.addEventListener('click', function (e) {
      var swatch = e.target.closest && e.target.closest('[data-cd-addon-swatch]');
      if (swatch) {
        var swatchCard = swatch.closest('[data-cd-addon]');
        if (swatchCard) syncSwatch(swatchCard, swatch);
        return;
      }

      var typeChoice = e.target.closest && e.target.closest('[data-cd-amodal-type]');
      if (typeChoice) {
        activeType = typeChoice.getAttribute('data-cd-amodal-type') || 'recommended';
        renderCurrentRecommendations();
        return;
      }

      var add = e.target.closest && e.target.closest('[data-cd-addon-add]');
      if (add) {
        var card = add.closest('[data-cd-addon]');
        if (card) addAddon(card);
      }
    });

    modal.addEventListener('change', function (e) {
      if (!e.target || !e.target.matches) return;
      if (e.target.matches('select[data-cd-addon-variant]')) {
        var card = e.target.closest('[data-cd-addon]');
        if (card) syncVariant(card);
      }
    });

    /* assets/cadoo.js announces every overlay close through this event, so the
       modal does not need to intercept the close button, the scrim, Escape or
       a programmatic close separately. */
    document.addEventListener('cadoo:panel-closed', function (e) {
      if (e && e.detail && e.detail.panel === modal) onClosed();
    });

    /* Window, capture phase: assets/cadoo.js handles Quick Add on document in
       the capture phase and stops the event there, so this must see the click
       first, while the control is still idle - a control that is already busy
       is skipped, exactly as assets/cadoo.js will refuse it. This only starts
       the offer request early; the add itself, its guards and its outcome
       stay entirely with assets/cadoo.js. A control that declares a role
       other than a main gift never opens this modal, so it is skipped; one
       with no role goes through the role check, which reads the same offer. */
    window.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-cd-quickadd]');
      if (!b || b.disabled || b.__cdQuickAddPending) return;
      if (b.hasAttribute('data-cd-role') && !isMainGiftRole(b.getAttribute('data-cd-role'))) return;
      if (pageOffer(b.getAttribute('data-cd-product-id'))) return;
      var url = b.getAttribute('data-cd-url');
      if (url) requestEarly(url);
    }, true);

    window.CadooAddonModal = {
      open: open,
      openDeferred: openDeferred,
      isMainGiftRole: isMainGiftRole
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

